import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const injectScript = (id, src, attrs = {}) => {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  if (src) script.src = src;
  Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
  document.head.appendChild(script);
  return script;
};

// Tracking/analytics IDs are admin-configured (Command Centre Settings
// module) rather than arbitrary visitor input, but they are still
// interpolated into raw inline script strings below -- so they are
// strictly allow-listed to a safe character set before ever reaching
// injectInlineScript. Any value containing characters outside this set
// (e.g. quotes, angle brackets, semicolons) is treated as invalid and
// discarded rather than injected, closing off the actual injection
// vector even if a malicious value were ever stored.
const sanitizeTrackingId = (rawId) => {
  if (typeof rawId !== "string") return "";
  const cleaned = rawId.trim();
  return /^[A-Za-z0-9_-]+$/.test(cleaned) ? cleaned : "";
};

const injectInlineScript = (id, content) => {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  // textContent (not innerHTML) -- this is a <script> tag, not markup;
  // textContent sets the raw script body without going through the
  // HTML parser, which is the correct API for this and avoids the
  // innerHTML anti-pattern entirely.
  script.textContent = content;
  document.head.appendChild(script);
};

// Each vendor's inline init snippet is a long, mostly-boilerplate
// string literal (not real branching logic) -- extracted into named
// pure builders so the effect below stays a short list of "if (id)
// inject" calls rather than five large template literals inlined
// directly in the effect body.
//
// NOTE on GA4: the Google tag (gtag.js) is now a PERMANENT, hardcoded
// snippet in public/index.html (per explicit instruction: exactly one
// Google tag per page, installed immediately after <head>). It is
// intentionally NOT also injected dynamically here -- doing so would
// load a second gtag.js instance and double-count every pageview/event
// if an admin ever also filled in a GA4 ID via the Settings module.
const buildGtmInitScript = (id) =>
  `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;

const buildMetaPixelInitScript = (id) =>
  `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', '${id}');fbq('track', 'PageView');`;

const buildLinkedinInitScript = (id) =>
  `_linkedin_partner_id = "${id}";window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];window._linkedin_data_partner_ids.push(_linkedin_partner_id);`;

const buildClarityInitScript = (id) =>
  `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window, document, "clarity", "script", "${id}");`;

const buildOrganizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      name: "Gopi Gadhvi Group",
      url: "https://www.gopigadhvi.com",
      slogan: "Building Trust. Creating Legacy.",
      address: { "@type": "PostalAddress", addressLocality: "Ahmedabad", addressRegion: "Gujarat", addressCountry: "IN" },
    },
    {
      "@type": "WebSite",
      name: "Gopi Gadhvi Group",
      url: "https://www.gopigadhvi.com",
    },
    {
      "@type": "Person",
      name: "Gopi Gadhvi",
      jobTitle: "Founder & CEO",
      worksFor: { "@type": "Organization", name: "Gopi Gadhvi Group" },
    },
  ],
});

// Conditionally injects GTM / Meta Pixel / LinkedIn Insight / Microsoft
// Clarity tags, but ONLY once an ID has been configured via the Command
// Centre Settings module. GA4 is NOT included here -- it is a permanent,
// hardcoded tag in public/index.html (see note above buildGtmInitScript).
// All others are empty by default -- this component is pure
// architecture/scaffolding until real tracking IDs are supplied ahead of
// launch. Also emits Organization + WebSite JSON-LD for baseline
// structured data / AI-search readiness.
const SiteScripts = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then((res) => setSettings(res.data)).catch(() => setSettings({}));
  }, []);

  useEffect(() => {
    if (!settings) return;

    const gtmId = sanitizeTrackingId(settings.gtm_container_id);
    const metaPixelId = sanitizeTrackingId(settings.meta_pixel_id);
    const linkedinId = sanitizeTrackingId(settings.linkedin_insight_tag_id);
    const clarityId = sanitizeTrackingId(settings.microsoft_clarity_id);

    if (gtmId) {
      injectInlineScript("gg-gtm-init", buildGtmInitScript(gtmId));
    }

    if (metaPixelId) {
      injectInlineScript("gg-meta-pixel-init", buildMetaPixelInitScript(metaPixelId));
    }

    if (linkedinId) {
      injectInlineScript("gg-linkedin-init", buildLinkedinInitScript(linkedinId));
      injectScript("gg-linkedin-insight", "https://snap.licdn.com/li.lms-analytics/insight.min.js");
    }

    if (clarityId) {
      injectInlineScript("gg-clarity-init", buildClarityInitScript(clarityId));
    }
  }, [settings]);

  useEffect(() => {
    let el = document.getElementById("gg-ld-json");
    if (!el) {
      el = document.createElement("script");
      el.id = "gg-ld-json";
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildOrganizationJsonLd());
  }, []);

  return null;
};

export default SiteScripts;
